import BonesMaterial from '@cartwhl/3d/threejs/materials/BonesMaterial';
import { useEffect, useRef } from 'react';
import * as THREE from 'three';
import * as BufferGeometryUtils from 'three/addons/utils/BufferGeometryUtils.js';

import { CartwheelBoneKey } from '../types';

const ARMATURE_RATIO = 0.9;

export default function Skeleton({
  object,
  assignedBones,
  highlightBones,
  meshOpacity = 0.3,
  renderOrder = 1,
  hideBones = false,
}: {
  object: THREE.Object3D;
  assignedBones: string[];
  highlightBones: string[];
  meshOpacity?: number;
  renderOrder?: number;
  hideBones?: boolean;
}) {
  const armatures = useRef<Record<string, THREE.Object3D>>({});
  const bones = useRef<Record<string, THREE.Bone>>({});

  useEffect(() => {
    if (!object) return;

    object.traverse((child) => {
      if (child instanceof THREE.Mesh && child.visible) {
        child.material.transparent = true;
        child.material.opacity = meshOpacity;
      }
    });

    return () => {
      object.traverse((child) => {
        if (child instanceof THREE.Mesh && child.visible) {
          child.material.opacity = 1;
        }
      });
    };
  }, [object, meshOpacity]);

  useEffect(() => {
    if (!object) return;

    object.traverse((bone) => {
      if (bone instanceof THREE.Bone) {
        // First, try to find a bone that we know is a CartwheelBoneKey
        let childBone = bone.children.find(
          (e) =>
            e.type === 'Bone' &&
            Object.values(CartwheelBoneKey).includes(
              e.name as CartwheelBoneKey,
            ),
        );

        // If we don't find one, just use the first child bone
        if (!childBone) {
          childBone = bone.children.find((e) => e.type === 'Bone');
        }

        // Get bone and parent positions
        const boneWorldPos = new THREE.Vector3();
        boneWorldPos.setFromMatrixPosition(bone.matrixWorld);

        const childWorldPos = new THREE.Vector3();
        if (childBone) {
          childBone.getWorldPosition(childWorldPos);
        } else {
          // Default to pointing upwards
          childWorldPos.copy(boneWorldPos).add(new THREE.Vector3(0, 0.1, 0));
        }

        const direction = new THREE.Vector3().subVectors(
          childWorldPos,
          boneWorldPos,
        );

        const boneLength = direction.length();
        direction.normalize();

        // Set rotation to align with bone direction
        const quaternion = new THREE.Quaternion();
        quaternion.setFromUnitVectors(new THREE.Vector3(0, 1, 0), direction);

        // Add armature to the bone
        const armature = armatureShape(boneLength, ARMATURE_RATIO);
        const material = new BonesMaterial({
          color: new THREE.Color('black'),
          opacity: 0.5,
        });
        const armatureMesh = new THREE.Mesh(armature, material);
        armatureMesh.renderOrder = renderOrder;

        armatureMesh.quaternion.copy(quaternion);

        bone.add(armatureMesh);

        armatures.current[bone.name] = armatureMesh;
        bones.current[bone.name] = bone;
      }
    });

    return () => {
      for (const key in armatures.current) {
        const armature = armatures.current[key];
        if (armature instanceof THREE.Mesh) {
          armature.removeFromParent();
          armature.geometry.dispose();
          armature.material.dispose();
        }
      }
    };
  }, [object, renderOrder]);

  useEffect(() => {
    for (const key in armatures.current) {
      const armature = armatures.current[key];
      if (armature instanceof THREE.Mesh) {
        const isHighlighted = highlightBones.includes(key);
        const isAssigned = assignedBones.includes(key);
        const material = armature.material as BonesMaterial;

        if (isHighlighted) {
          material.opacity = 0.9;
          material.uniforms.color.value.set('#1478FF');
          armature.renderOrder = renderOrder + 2;
        } else if (isAssigned) {
          material.opacity = 0.9;
          material.uniforms.color.value.set('#65F157');
          armature.renderOrder = renderOrder + 1;
        } else {
          material.opacity = 0.5;
          material.uniforms.color.value.set('#000000');
          armature.renderOrder = renderOrder;
        }
        material.needsUpdate = true;
      }
    }
  }, [assignedBones, highlightBones, renderOrder]);

  useEffect(() => {
    for (const key in armatures.current) {
      const armature = armatures.current[key];
      if (armature instanceof THREE.Mesh) {
        armature.visible = !hideBones;
      }
    }
  }, [hideBones]);

  return null;
}

function armatureShape(length: number, ratio: number) {
  const geo = new THREE.BufferGeometry();

  const b = 0.03;

  // prettier-ignore
  const vertices = new Float32Array([
      // Top vertices (first 5 vertices):
      0, length, 0,          // Vertex 0: Top point
      1, b, 0,             // Vertex 1: Front right
      -1, b, 0,            // Vertex 2: Front left
      0, b, 1,             // Vertex 3: Back right
      0, b, -1,            // Vertex 4: Back left
      // Bottom vertices (next 5 vertices):
      0, 0, 0,    // Vertex 5: Bottom point
      1, b, 0,             // Vertex 6: Front right
      -1, b, 0,            // Vertex 7: Front left
      0, b, 1,             // Vertex 8: Back right
      0, b, -1,            // Vertex 9: Back left
    ]);

  // prettier-ignore
  const indices = new Uint16Array([
      // Top faces (all counter-clockwise when viewed from outside):
      0, 4, 1,  // Top front
      0, 3, 1,  // Top right
      0, 2, 3,  // Top back
      0, 4, 2,  // Top left
      // Bottom faces (all counter-clockwise when viewed from outside):
      5, 6, 9,  // Bottom front
      5, 9, 7,  // Bottom right
      5, 8, 7,  // Bottom back
      5, 8, 6   // Bottom left
    ]);

  // prettier-ignore
  const uvs = new Float32Array([
    // Top vertices UVs
    0.5, 0.0, // Top point
    1.0, 0.5, // Front right
    0.0, 0.5, // Front left
    0.75, 0.75, // Back right
    0.25, 0.75, // Back left
    // Bottom vertices UVs
    0.5, 1.0, // Bottom point
    1.0, 0.5, // Front right
    0.0, 0.5, // Front left
    0.75, 0.75, // Back right
    0.25, 0.75, // Back left
  ]);

  geo.setAttribute('position', new THREE.BufferAttribute(vertices, 3));
  geo.setAttribute('uv', new THREE.BufferAttribute(uvs, 2));
  geo.setIndex(new THREE.BufferAttribute(indices, 1));
  geo.computeVertexNormals();

  geo.scale(length * 0.1, 1.0, length * 0.1);

  // Add sphere to the geo
  const topSphere = new THREE.SphereGeometry(Math.min(length * 0.07, 0.01));
  const bottomSphere = new THREE.SphereGeometry(Math.min(length * 0.07, 0.01));

  // For the top sphere, place it at the top vertex position
  topSphere.translate(0, length, 0);

  const mergedGeometry = BufferGeometryUtils.mergeGeometries([
    geo,
    topSphere,
    bottomSphere,
  ]);

  return mergedGeometry;
}
